/**
 * Layout system state and action types
 *
 * Manages sidebar visibility (mobile drawer vs desktop collapsed),
 * and responsive breakpoint detection.
 */

/**
 * Layout state managed by LayoutContext
 */
export interface LayoutState {
    /** Mobile: sidebar drawer is visible */
    sidebarOpen: boolean;
    /** Desktop: sidebar is in narrow/icon-only mode */
    sidebarCollapsed: boolean;
    /** Viewport is below lg breakpoint (< 992px) */
    isMobile: boolean;
    /** Viewport is between md and lg breakpoints (768px - 991px) */
    isTablet: boolean;
    /** Viewport is at or above lg breakpoint (>= 992px) */
    isDesktop: boolean;
}

/**
 * Actions dispatched to the layout reducer
 */
export type LayoutAction =
    | { type: 'TOGGLE_SIDEBAR' }
    | { type: 'OPEN_SIDEBAR' }
    | { type: 'CLOSE_SIDEBAR' }
    | { type: 'COLLAPSE_SIDEBAR' }
    | { type: 'EXPAND_SIDEBAR' }
    | { type: 'TOGGLE_COLLAPSE' }
    | { type: 'SET_BREAKPOINT'; payload: { isMobile: boolean; isTablet: boolean } };

/**
 * Actions exposed by useLayout hook
 */
export interface LayoutActions {
    /** Toggle sidebar open/closed (mobile) or collapsed/expanded (desktop) */
    toggleSidebar: () => void;
    /** Open sidebar drawer (mobile) */
    openSidebar: () => void;
    /** Close sidebar drawer (mobile) */
    closeSidebar: () => void;
    /** Collapse sidebar to narrow mode (desktop) */
    collapseSidebar: () => void;
    /** Expand sidebar to full width (desktop) */
    expandSidebar: () => void;
    /** Toggle between collapsed and expanded (desktop) */
    toggleCollapse: () => void;
}

/**
 * Combined value returned by useLayout hook
 */
export interface LayoutContextValue {
    state: LayoutState;
    actions: LayoutActions;
}

/**
 * localStorage key for persisting collapsed state
 */
export const SIDEBAR_COLLAPSED_KEY = 'layout-sidebar-collapsed';

/**
 * Viewport breakpoints in px, mirroring `$breakpoints` in styles/breakpoints.scss so the layout
 * context switches at the same widths as the stylesheets. breakpoints.test.ts compiles the SCSS
 * map and fails if the two differ.
 */
export const BREAKPOINTS = {
    xxxs: 0,
    xxs: 320,
    xs: 568,
    sm: 667,
    md: 768,
    lg: 992,
    xl: 1200,
    xxl: 1440,
    xxxl: 1600,
    xxxxl: 1920,
} as const;
